import React, { useState } from 'react';
import emailjs from '@emailjs/browser';

const INITIAL = { name: '', company: '', email: '', message: '' };

function Field({ label, name, type = 'text', value, onChange, required, placeholder }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor={`contact-${name}`}
        className="font-sans text-[0.6875rem] font-medium tracking-[0.1em] uppercase text-ink-muted"
      >
        {label}
        {required && <span className="text-ink-muted ml-0.5" aria-hidden="true"> *</span>}
      </label>
      <input
        id={`contact-${name}`}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className="bg-parchment-mid border border-border-warm rounded-[2px] px-3.5 py-2.5 font-sans text-base text-ink-body placeholder:text-ink-muted/60 focus:outline-none focus:border-ink-deep transition-colors duration-150 w-full"
      />
    </div>
  );
}

export default function ContactForm() {
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState('idle'); // idle | loading | success | error

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          from_name: form.name,
          from_company: form.company,
          from_email: form.email,
          message: form.message,
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );
      setStatus('success');
      setForm(INITIAL);
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="border border-border-warm rounded-[2px] p-8 sm:p-10 text-center">
        <div className="w-8 h-px bg-gold-edge mx-auto mb-6" />
        <p className="font-serif text-2xl font-light text-ink-deep mb-3">
          Request received.
        </p>
        <p className="font-sans text-base text-ink-muted leading-[1.65]">
          We'll be in touch shortly to discuss your requirements.
        </p>
        <button
          onClick={() => setStatus('idle')}
          className="mt-6 font-sans text-[0.6875rem] font-medium tracking-[0.08em] uppercase text-ink-muted hover:text-ink-deep transition-colors underline underline-offset-2"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field
          label="Name"
          name="name"
          value={form.name}
          onChange={handleChange}
          required
          placeholder="Your name"
        />
        <Field
          label="Company"
          name="company"
          value={form.company}
          onChange={handleChange}
          placeholder="Your company"
        />
      </div>

      <Field
        label="Email"
        name="email"
        type="email"
        value={form.email}
        onChange={handleChange}
        required
        placeholder="you@company.com"
      />

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="contact-message"
          className="font-sans text-[0.6875rem] font-medium tracking-[0.1em] uppercase text-ink-muted"
        >
          Message <span className="text-ink-muted" aria-hidden="true"> *</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          value={form.message}
          onChange={handleChange}
          required
          rows={5}
          placeholder="Tell us what you're looking for — product, volume, market, and timeline."
          className="bg-parchment-mid border border-border-warm rounded-[2px] px-3.5 py-2.5 font-sans text-base text-ink-body placeholder:text-ink-muted/60 focus:outline-none focus:border-ink-deep transition-colors duration-150 resize-none w-full"
        />
      </div>

      {status === 'error' && (
        <p className="font-sans text-[0.6875rem] text-[#c0392b] tracking-[0.05em]" role="alert">
          Something went wrong. Please try again or contact us directly.
        </p>
      )}

      <div className="pt-1">
        <button
          type="submit"
          disabled={status === 'loading'}
          className="bg-ink-deep text-parchment px-8 py-3 rounded-[2px] font-sans text-[0.6875rem] font-medium tracking-[0.1em] uppercase transition-colors duration-[180ms] hover:bg-gold-edge disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold-edge focus-visible:outline-offset-2"
        >
          {status === 'loading' ? 'Sending…' : 'Send Request'}
        </button>
      </div>
    </form>
  );
}
